import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { changeOwnPassword, updateProfile } from '../../api';
import Modal from '../../components/Modal';
import Alert from '../../components/Alert';
import {
  KeyIcon,
  PhotoIcon,
  UserIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

export default function ProfilePage() {
  const { user, loginUser } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    try {
      await changeOwnPassword(passwordForm.currentPassword, passwordForm.newPassword);
      setShowPasswordModal(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSuccess('Password changed successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be less than 10MB');
      return;
    }

    setAvatarLoading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const res = await updateProfile({ avatar: reader.result });
        const token = localStorage.getItem('token');
        loginUser({ ...user, avatar: res.data.avatar }, token);
        setSuccess('Avatar updated!');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to update avatar');
      } finally {
        setAvatarLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = async () => {
    try {
      const res = await updateProfile({ avatar: '' });
      const token = localStorage.getItem('token');
      loginUser({ ...user, avatar: '' }, token);
      setSuccess('Avatar removed!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove avatar');
    }
  };

  const handleDisplayNameSave = async () => {
    if (!newDisplayName.trim()) return;
    try {
      const res = await updateProfile({ displayName: newDisplayName.trim() });
      const token = localStorage.getItem('token');
      loginUser({ ...user, displayName: res.data.displayName }, token);
      setEditingDisplayName(false);
      setSuccess('Display name updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update display name');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Account Settings</h2>
          <div className="subtitle">Manage your profile and security</div>
        </div>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <div className="profile-section">
        {/* Avatar Section */}
        <div className="profile-avatar-section">
          <div className="profile-avatar">
            {user?.avatar ? (
              <img src={user.avatar} alt="avatar" />
            ) : (
              user?.displayName?.charAt(0)?.toUpperCase() || 'U'
            )}
          </div>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>{user?.displayName}</h3>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>@{user?.username} • {user?.role}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                <PhotoIcon className="icon" /> {avatarLoading ? 'Uploading...' : 'Upload Avatar'}
                <input type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display: 'none' }} disabled={avatarLoading} />
              </label>
              {user?.avatar && (
                <button className="btn btn-ghost btn-sm" onClick={handleRemoveAvatar} style={{ color: 'var(--danger)' }}>
                  Remove
                </button>
              )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
              Max 10MB
            </div>
          </div>
        </div>

        {/* Account Info */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserIcon className="icon" /> Account Information
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-primary)', borderRadius: 'var(--radius)' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Username</span>
              <span style={{ fontSize: 14, fontWeight: 500 }}>{user?.username}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'var(--bg-primary)', borderRadius: 'var(--radius)' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Display Name</span>
              {editingDisplayName ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    className="form-input"
                    style={{ padding: '4px 8px', fontSize: 14, width: 180 }}
                    value={newDisplayName}
                    onChange={e => setNewDisplayName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleDisplayNameSave(); if (e.key === 'Escape') setEditingDisplayName(false); }}
                    autoFocus
                  />
                  <button className="btn btn-primary btn-sm" onClick={handleDisplayNameSave}>Save</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditingDisplayName(false)}>Cancel</button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{user?.displayName}</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setNewDisplayName(user?.displayName || ''); setEditingDisplayName(true); }}>Edit</button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-primary)', borderRadius: 'var(--radius)' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Role</span>
              <span style={{ fontSize: 14, fontWeight: 500, textTransform: 'capitalize' }}>{user?.role}</span>
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="card">
          <div className="card-header">
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <KeyIcon className="icon" /> Security
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>Password</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Change your account password</div>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowPasswordModal(true)}>
              <KeyIcon className="icon" /> Change Password
            </button>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      <Modal isOpen={showPasswordModal} onClose={() => { setShowPasswordModal(false); setError(''); }} title="Change Password"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => { setShowPasswordModal(false); setError(''); }}>Cancel</button>
            <button className="btn btn-primary" onClick={handlePasswordChange}>
              <CheckCircleIcon className="icon" /> Update Password
            </button>
          </>
        }
      >
        {error && <Alert type="error" message={error} onClose={() => setError('')} />}
        <form onSubmit={handlePasswordChange}>
          <div className="form-group">
            <label>Current Password</label>
            <input
              type="password"
              className="form-input"
              value={passwordForm.currentPassword}
              onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              className="form-input"
              value={passwordForm.newPassword}
              onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              placeholder="Minimum 6 characters"
              required
            />
          </div>
          <div className="form-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              className="form-input"
              value={passwordForm.confirmPassword}
              onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              required
            />
          </div>
        </form>
      </Modal>
    </div>
  );
}
